import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { CalendarCheck, CalendarX, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { DateInputBR } from '@components/DateInputBR';
import { MONTHS_PT } from '@components/dateInputUtils';
import { buildCategoryTree, categoryOptionLabel, flattenCategoryTree } from '@services/categoryService';
import transactionService from '@services/transactionService';
import { INVOICE_STATUS_LABELS, MAX_SCHEDULE_LEAD_DAYS, SCHEDULE_STATUS_LABELS, clampLeadDays, type Category, type InvoiceStatus, type Movement, type ScheduleStatus, type Transaction } from '@/types/finance';
import { colorClasses } from '@/utils/colors';
import { competenceFromYearMonth, competenceToYearMonth, currentCompetence, isValidCompetence, type Competence } from '@/utils/competence';
import { formatBRL } from '@/utils/money';
import { currentMonth, monthRange, rangeAsMonth, rangeLabel, shiftMonth, type DateRange } from '@/utils/period';

// Pedaços de UI compartilhados pelas telas de Finanças (contas, lançamentos, categorias, importação).

/** Valor em R$ colorido pelo sinal (vermelho saída, verde entrada); previsto em itálico. */
export const AmountText: React.FC<{ cents: number; projected?: boolean; muted?: boolean; className?: string }> = ({
  cents,
  projected,
  muted,
  className,
}) => (
  <span
    className={clsx(
      'tabular-nums whitespace-nowrap',
      muted ? 'text-slate-600' : cents < 0 ? 'text-red-600' : cents > 0 ? 'text-emerald-700' : 'text-slate-500',
      projected && 'italic',
      className,
    )}
  >
    {cents > 0 && !muted ? '+' : ''}
    {formatBRL(cents)}
  </span>
);

/** Saldo (sem sinal "+"): negativo em vermelho, positivo em texto escuro. */
export const BalanceText: React.FC<{ cents: number; className?: string }> = ({ cents, className }) => (
  <span className={clsx('tabular-nums whitespace-nowrap', cents < 0 ? 'text-red-600' : 'text-slate-800', className)}>{formatBRL(cents)}</span>
);

/** Bolinha com a cor da paleta. */
export const ColorDot: React.FC<{ color?: string | null; className?: string }> = ({ color, className }) => (
  <span className={clsx('inline-block w-2.5 h-2.5 rounded-full flex-shrink-0', colorClasses(color).swatch, className)} />
);

/** Nome com a bolinha da cor (conta, categoria). */
export const ColorChip: React.FC<{ color?: string | null; children: React.ReactNode; className?: string }> = ({ color, children, className }) => (
  <span className={clsx('inline-flex items-center gap-1.5 min-w-0', className)}>
    <ColorDot color={color} />
    <span className="truncate">{children}</span>
  </span>
);

/** Etiqueta "Previsto" para lançamentos com data futura. */
export const ProjectedBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span className={clsx('badge-info', className)} title="Data depois de hoje: ainda não realizado">
    Previsto
  </span>
);

const SCHEDULE_BADGE: Record<Exclude<ScheduleStatus, 'none'>, string> = {
  scheduled: 'badge-success',
  to_schedule: 'badge-warning',
  missed: 'badge-danger',
  upcoming: 'badge-neutral',
};

const SCHEDULE_HINT: Record<Exclude<ScheduleStatus, 'none'>, string> = {
  scheduled: 'Pagamento agendado no banco (ou já realizado pelo extrato)',
  to_schedule: 'Pago por agendamento: está na hora de agendar no banco',
  missed: 'Venceu e não foi marcada como agendada',
  upcoming: 'Pago por agendamento: o aviso começa alguns dias antes do vencimento',
};

/**
 * Fase 7 — situação do agendamento no banco (derivada pelo backend): Agendada verde · A agendar
 * âmbar · Venceu sem agendar vermelha · Aguardando neutra. `none` (ou ausente) não mostra nada.
 */
export const ScheduleBadge: React.FC<{ status?: ScheduleStatus | null; className?: string }> = ({ status, className }) => {
  if (!status || status === 'none') return null;
  return (
    <span className={clsx(SCHEDULE_BADGE[status], 'whitespace-nowrap', className)} title={SCHEDULE_HINT[status]}>
      {SCHEDULE_STATUS_LABELS[status]}
    </span>
  );
};

/**
 * Botão de linha "Marcar como agendada / Desmarcar agendamento" (POST /transactions/:id/schedule).
 * Não abre o formulário; devolve o lançamento atualizado em `onDone`.
 */
export const ScheduleToggleButton: React.FC<{
  transaction: Pick<Transaction, 'id' | 'scheduled_at' | 'schedule_status'>;
  onDone: (updated: Transaction) => void;
  onError: (err: unknown) => void;
  /** Mostra o rótulo ao lado do ícone. */
  showLabel?: boolean;
}> = ({ transaction, onDone, onError, showLabel }) => {
  const [busy, setBusy] = useState(false);
  const scheduled = !!transaction.scheduled_at;
  const label = scheduled ? 'Desmarcar agendamento' : 'Marcar como agendada';
  const click = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      onDone(await transactionService.schedule(transaction.id, !scheduled));
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      title={label}
      aria-label={label}
      className={clsx(
        'p-2 rounded-md transition-all duration-200 disabled:opacity-50 inline-flex items-center gap-1 text-xs font-medium',
        scheduled ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-50' : 'text-emerald-700 hover:bg-emerald-50',
      )}
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : scheduled ? <CalendarX size={16} /> : <CalendarCheck size={16} />}
      {showLabel && <span>{scheduled ? 'Desmarcar' : 'Agendada'}</span>}
    </button>
  );
};

/**
 * Fase 7 — "pago por agendamento no banco": caixa + "Avisar __ dias antes" (0..30, padrão 5).
 * `label` muda o texto da caixa (recorrência × lançamento avulso).
 */
export const ScheduleFields: React.FC<{
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  leadDays: number;
  onLeadDaysChange: (v: number) => void;
  label: string;
  hint?: React.ReactNode;
  disabled?: boolean;
}> = ({ checked, onCheckedChange, leadDays, onLeadDaysChange, label, hint, disabled }) => {
  // Rascunho em texto: deixa apagar e redigitar; o valor válido (0..30) sobe a cada tecla e o blur normaliza.
  const [draft, setDraft] = useState(String(leadDays));
  return (
  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
      <input type="checkbox" className="checkbox-field" checked={checked} disabled={disabled} onChange={(e) => onCheckedChange(e.target.checked)} />
      <CalendarCheck size={15} className="text-slate-500" />
      {label}
    </label>
    {checked && (
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span>Avisar</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_SCHEDULE_LEAD_DAYS}
          className="input-field w-20"
          value={draft}
          disabled={disabled}
          aria-label="Dias de antecedência do aviso"
          onChange={(e) => {
            setDraft(e.target.value);
            if (e.target.value.trim() !== '') onLeadDaysChange(clampLeadDays(e.target.value));
          }}
          onBlur={() => {
            const v = clampLeadDays(draft);
            setDraft(String(v));
            onLeadDaysChange(v);
          }}
        />
        <span>dias antes do vencimento</span>
      </div>
    )}
    {hint && <p className="text-small">{hint}</p>}
  </div>
  );
};

interface PeriodPickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Período com atalhos de mês: ‹ mês anterior · rótulo ("novembro de 2026" ou dd/MM/aaaa a
 * dd/MM/aaaa) · próximo ›, mais "Mês atual" e os dois DateInputBR para intervalo livre.
 */
export const PeriodPicker: React.FC<PeriodPickerProps> = ({ value, onChange, disabled, className }) => {
  const ym = rangeAsMonth(value) ?? (value.start ? (() => {
    const m = /^(\d{4})-(\d{2})/.exec(value.start);
    return m ? { year: Number(m[1]), month: Number(m[2]) } : currentMonth();
  })() : currentMonth());
  const go = (delta: number) => {
    const next = shiftMonth(ym, delta);
    onChange(monthRange(next.year, next.month));
  };
  const today = currentMonth();
  const isCurrent = !!rangeAsMonth(value) && ym.year === today.year && ym.month === today.month;

  return (
    <div className={clsx('grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2', className)}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
          onClick={() => go(-1)}
          disabled={disabled}
          aria-label="Mês anterior"
          title="Mês anterior"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="flex-1 sm:flex-none min-w-[11rem] text-center inline-block text-sm font-medium text-slate-800 first-letter:uppercase">{rangeLabel(value)}</span>
        <button
          type="button"
          className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
          onClick={() => go(1)}
          disabled={disabled}
          aria-label="Próximo mês"
          title="Próximo mês"
        >
          <ChevronRight size={16} />
        </button>
        {!isCurrent && (
          <button
            type="button"
            className="ml-1 text-xs text-emerald-700 hover:underline whitespace-nowrap"
            onClick={() => onChange(monthRange(today.year, today.month))}
            disabled={disabled}
          >
            Mês atual
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <DateInputBR className="input-field w-full sm:w-36" value={value.start} onChange={(v) => onChange({ ...value, start: v })} disabled={disabled} />
        <span className="text-small">a</span>
        <DateInputBR
          className="input-field w-full sm:w-36"
          value={value.end}
          min={value.start || undefined}
          onChange={(v) => onChange({ ...value, end: v })}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export interface PillTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
}

/** Abas controladas (segmentadas), para quando a tela precisa trocar de aba por código. */
export const PillTabs: React.FC<{ tabs: PillTab[]; active: string; onChange: (id: string) => void; className?: string }> = ({
  tabs,
  active,
  onChange,
  className,
}) => (
  <div className={clsx('border-b border-slate-200 overflow-x-auto', className)}>
    <nav className="flex gap-1 min-w-max" aria-label="Abas">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={clsx(
            'inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
            active === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
          )}
        >
          {t.icon}
          {t.label}
          {t.count !== undefined && <span className="badge-neutral">{t.count}</span>}
        </button>
      ))}
    </nav>
  </div>
);

const INVOICE_STATUS_BADGE: Record<InvoiceStatus, string> = {
  open: 'text-blue-700 bg-blue-100',
  closed: 'text-amber-700 bg-amber-100',
  due: 'text-orange-700 bg-orange-100',
  overdue: 'text-red-700 bg-red-100',
  paid: 'text-green-700 bg-green-100',
};

const INVOICE_STATUS_HINT: Record<InvoiceStatus, string> = {
  open: 'Ainda recebe compras (antes do fechamento)',
  closed: 'Fechada: aguardando o vencimento',
  due: 'Vence hoje',
  overdue: 'Vencida e ainda não paga',
  paid: 'Paga',
};

/** Situação da fatura (derivada pelo backend): Aberta azul · Fechada âmbar · Vence hoje laranja · Vencida vermelha · Paga verde. */
export const InvoiceStatusBadge: React.FC<{ status: InvoiceStatus; className?: string }> = ({ status, className }) => (
  <span className={clsx('inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap', INVOICE_STATUS_BADGE[status], className)} title={INVOICE_STATUS_HINT[status]}>
    {INVOICE_STATUS_LABELS[status]}
  </span>
);

interface CompetencePickerProps {
  /** AAAAMM ou null (quando `allowEmpty`). */
  value: Competence | null;
  onChange: (c: Competence | null) => void;
  /** Mostra a opção "—" no mês (limpa o valor). */
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  /** Anos oferecidos em volta do ano corrente (padrão −5..+10). */
  yearsBack?: number;
  yearsAhead?: number;
  className?: string;
}

/** Mês/ano em dois selects (competência AAAAMM). Sem `<input type="month">` (locale do navegador). */
export const CompetencePicker: React.FC<CompetencePickerProps> = ({
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = '—',
  disabled,
  yearsBack = 5,
  yearsAhead = 10,
  className,
}) => {
  const now = competenceToYearMonth(currentCompetence());
  const ym = value !== null && isValidCompetence(value) ? competenceToYearMonth(value) : null;
  const years = useMemo(() => {
    const list: number[] = [];
    const from = Math.min(now.year - yearsBack, ym?.year ?? now.year);
    const to = Math.max(now.year + yearsAhead, ym?.year ?? now.year);
    for (let y = from; y <= to; y++) list.push(y);
    return list;
  }, [now.year, yearsBack, yearsAhead, ym?.year]);
  const set = (month: number | null, year: number) => {
    if (month === null) onChange(null);
    else onChange(competenceFromYearMonth({ year, month }));
  };
  return (
    <div className={clsx('grid grid-cols-[1fr_auto] gap-2', className)}>
      <select
        className="select-field"
        value={ym?.month ?? ''}
        disabled={disabled}
        aria-label="Mês"
        onChange={(e) => set(e.target.value ? Number(e.target.value) : null, ym?.year ?? now.year)}
      >
        {allowEmpty && <option value="">{emptyLabel}</option>}
        {!allowEmpty && !ym && <option value="">Mês…</option>}
        {MONTHS_PT.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        className="select-field w-24"
        value={ym?.year ?? now.year}
        disabled={disabled || (allowEmpty && !ym)}
        aria-label="Ano"
        onChange={(e) => set(ym?.month ?? now.month, Number(e.target.value))}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

/** Despesa / Receita (o sinal do valor é aplicado pelo service). `labels` troca os rótulos (ex.: Compra / Estorno). */
export const MovementToggle: React.FC<{
  value: 'expense' | 'income';
  onChange: (m: 'expense' | 'income') => void;
  disabled?: boolean;
  labels?: { expense: string; income: string };
}> = ({ value, onChange, disabled, labels = { expense: 'Despesa', income: 'Receita' } }) => (
  <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="radiogroup">
    {(['expense', 'income'] as const).map((m) => (
      <button
        key={m}
        type="button"
        role="radio"
        aria-checked={value === m}
        disabled={disabled}
        onClick={() => onChange(m)}
        className={clsx(
          'px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60',
          value === m ? (m === 'expense' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white') : 'bg-white text-slate-600 hover:bg-slate-50',
        )}
      >
        {labels[m]}
      </button>
    ))}
  </div>
);

interface CategorySelectProps {
  categories: Category[];
  /** Filtra pelo movimento (sem filtro = todas as categorias que não são de transferência). */
  movement?: Movement | '';
  value: number | '';
  onChange: (id: number | '') => void;
  disabled?: boolean;
  emptyLabel?: string;
  /** Inclui desativadas (padrão: só ativas, mais a selecionada). */
  includeDisabled?: boolean;
  className?: string;
}

/** Select de categoria com a árvore recuada ("↳ filha"); exclui as de transferência por padrão. */
export const CategorySelect: React.FC<CategorySelectProps> = ({
  categories,
  movement,
  value,
  onChange,
  disabled,
  emptyLabel = 'Sem categoria',
  includeDisabled = false,
  className,
}) => {
  const options = useMemo(() => {
    const base = categories.filter((c) => (movement ? c.movement === movement : c.movement !== 'transfer'));
    const usable = base.filter((c) => includeDisabled || !c.is_disabled || c.id === value);
    return flattenCategoryTree(buildCategoryTree(usable));
  }, [categories, movement, includeDisabled, value]);
  return (
    <select className={clsx('select-field', className)} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}>
      <option value="">{emptyLabel}</option>
      {options.map(({ category, depth }) => (
        <option key={category.id} value={category.id}>
          {categoryOptionLabel(category, depth)}
          {category.is_disabled ? ' (desativada)' : ''}
        </option>
      ))}
    </select>
  );
};

export const labelCls = 'text-label block mb-1';
export const Required: React.FC = () => <span className="text-red-500"> *</span>;
